import {
  RECEIVE_SESSION,
  REQUEST_SESSION_REFRESH,
  SESSION_BOOTSTRAP_TIMEOUT_MS,
  SESSION_QUERY_PARAM,
  SESSION_RECEIVED,
  SESSION_REFRESH_TIMEOUT_MS,
  STOREFRONT_SESSION_SIGNAL
} from '../constants/session'

let sessionToken = null
let sessionShop = null
let pendingRefresh = null
let stopListeningForSessions = null

const canReceiveParentSession = (params) => !params.get('token') && window.parent !== window

const isStorefrontSessionSignalled = (params) =>
  params.get(SESSION_QUERY_PARAM) === STOREFRONT_SESSION_SIGNAL

const isSessionMessage = (event) => {
  const token = event.data?.token

  return event.source === window.parent &&
    event.data?.type === RECEIVE_SESSION &&
    typeof token === 'string' &&
    token !== ''
}

const confirmSessionReceived = () => {
  const params = new URLSearchParams(window.location.search)

  if (!canReceiveParentSession(params) || !isStorefrontSessionSignalled(params)) return

  window.parent.postMessage({ type: SESSION_RECEIVED, origin: 'pinflag-shopify-pinmap-pro' }, '*')
}

const adoptSession = ({ token, shop }) => {
  sessionToken = token
  if (shop) sessionShop = shop
  confirmSessionReceived()
}

export const getSessionToken = () => sessionToken

export const getSessionShop = () => sessionShop

export const setSessionToken = (token) => {
  sessionToken = token
}

export const clearSession = () => {
  sessionToken = null
  sessionShop = null
  pendingRefresh = null
}

export const attachSessionHeader = (config) => {
  const newConfig = config

  if (!sessionToken || !newConfig.headers) return newConfig

  newConfig.headers['x-pinflag-session'] = sessionToken

  return newConfig
}

export const applySessionHeaders = (config) => {
  const newConfig = attachSessionHeader(config)

  if (!sessionToken || !newConfig.headers) return newConfig

  delete newConfig.headers['x-api-key']

  return newConfig
}

export const bootstrapSession = async (search = window.location.search) => {
  if (sessionToken) return sessionToken

  const params = new URLSearchParams(search)

  if (!canReceiveParentSession(params)) return null

  const timeoutMs = isStorefrontSessionSignalled(params)
    ? SESSION_REFRESH_TIMEOUT_MS
    : SESSION_BOOTSTRAP_TIMEOUT_MS

  return refreshSession(timeoutMs)
}

export const listenForPushedSessions = (search = window.location.search) => {
  if (stopListeningForSessions) return stopListeningForSessions

  if (!canReceiveParentSession(new URLSearchParams(search))) return () => {}

  const handlePushedSession = (event) => {
    if (isSessionMessage(event)) adoptSession(event.data)
  }

  window.addEventListener('message', handlePushedSession)
  stopListeningForSessions = () => {
    window.removeEventListener('message', handlePushedSession)
    stopListeningForSessions = null
  }

  return stopListeningForSessions
}

export const buildSessionRetryHandler = (instance, applyHeaders = applySessionHeaders) => async (error) => {
  const config = error.config

  if (error.response?.status !== 401 || !sessionToken || !config || config.sessionRetried) {
    return Promise.reject(error)
  }

  config.sessionRetried = true

  try {
    await refreshSession()
  } catch {
    return Promise.reject(error)
  }

  return instance(applyHeaders(config))
}

export const refreshSession = (timeoutMs = SESSION_REFRESH_TIMEOUT_MS) => {
  if (pendingRefresh) return pendingRefresh

  pendingRefresh = new Promise((resolve, reject) => {
    const handleMessage = (event) => {
      if (!isSessionMessage(event)) return

      cleanup()
      adoptSession(event.data)
      resolve(event.data.token)
    }

    const timeoutId = setTimeout(() => {
      cleanup()
      reject(new Error('Session refresh timed out'))
    }, timeoutMs)

    const cleanup = () => {
      clearTimeout(timeoutId)
      window.removeEventListener('message', handleMessage)
      pendingRefresh = null
    }

    window.addEventListener('message', handleMessage)
    // Carries no payload, so a wildcard target is safe here; the reply is what must be pinned
    window.parent.postMessage(
      { type: REQUEST_SESSION_REFRESH, hasSession: Boolean(sessionToken) },
      '*'
    )
  })

  return pendingRefresh
}
